<template>
  <div class="flex flex-col w-full h-full overflow-auto">
    <BaseTableControls
      v-if="!tableControlsHidden"
      v-model="searchQuery"
      :is-saving-layout="isSavingLayout"
      :isSavingLayoutHidden="isSavingLayoutHidden"
      :is-filters-active="filtersActive"
      :isFilterButtonHidden="isFilterButtonHidden || filterButtonHidden"
      :is-search-hidden="isSearchHidden"
      :only-search="onlySearch"
      :is-add-new-hidden="isAddNewHidden"
      :is-download-button-hidden="isDownloadButtonHidden"
      :is-refresh-button-hidden="isRefreshButtonHidden"
      :add-button-text="addButtonText"
      @save-layout="toggleSaveLayout"
      @filters-active="toggleFilters"
      @export-to-excel="onExportToExcel"
      @refresh-data="onRefreshData"
      @updateSearch="onSearchUpdate"
      @add-new="emit('addNew')">
      <template #default>
        <slot name="controls"></slot>
      </template>
    </BaseTableControls>
    <BaseTableMultiSelect
      v-if="multiSelectedItems.length"
      :hide-download-button="hideMultiselectDownload"
      :hide-delete-button="hideMultiselectDelete"
      @close="multiSelectedItems = []"
      @deleteMultiselect="modalMultiDeleteConfirmation = true"
      :selectedLength="multiSelectedItems.length"
      @export-to-excel="onExportToExcel"
      @emit-selected-items="handleExtraButtonClicked">
      <template #extraButtonContent>
        <slot name="extraButtonContent" />
      </template>
    </BaseTableMultiSelect>
    <slot name="top"></slot>
    <!-- Table Filters -->
    <div
      v-if="slots.filters && filtersActive"
      ref="accordionBody"
      id="accordion-flush-body-1"
      class="duration-[350ms] ease-[cubic-bezier(0.25, 0.1, 0.25, 0.1)] motion-reduce:transition-none flex flex-col"
      aria-labelledby="accordion-flush-heading-1">
      <div>
        <slot name="filters"></slot>
      </div>
      <div class="flex items-center justify-center w-full gap-4 pb-6 mt-auto md:col-span-4">
        <BaseButton color="primary" @click="onApplyFilters">{{
          $t('common.table.applyFilters')
        }}</BaseButton>
        <BaseButton color="primary" outline @click="emit('resetFilters')">{{
          $t('common.table.resetFilters')
        }}</BaseButton>
      </div>
    </div>

    <!-- Table Grouping -->
    <div ref="groupDropZone" class="relative pl-2 bg-white" v-if="!onlySearch && !hideGrouping">
      <template v-if="groupHeaders.length > 0">
        <draggable
          tag="div"
          group="a"
          v-model="groupHeaders"
          item-key="value"
          class="flex items-center justify-start gap-3 py-3">
          <template #item="{ element: tag }">
            <span
              class="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md cursor-pointer gap-x-4 bg-theme-100 text-theme-600"
              :key="tag.value"
              :draggable="isDragEnabled"
              @dragstart="onDragStart($event, tag)">
              {{ tag.text }}
              <button
                v-if="tag.isSortHidden"
                type="button"
                class="group relative -mr-1 h-3.5 w-3.5 rounded-sm"
                @click="toggleSortOrder(tag)">
                <IconDescAsc
                  class="transition-transform size-3"
                  :class="{
                    'rotate-0': isAscending(tag),
                    'rotate-180': !isAscending(tag),
                  }" />
                <span class="absolute -inset-1"></span>
              </button>
              <button
                type="button"
                class="group relative -mr-1 h-3.5 w-3.5 rounded-sm hover:bg-gray-500/20"
                @click="onUngroup(tag)">
                <IconXmark
                  class="h-3.5 w-3.5 stroke-theme-700/50 group-hover:stroke-theme-700/75" />
                <span class="absolute -inset-1"></span>
              </button>
            </span>
          </template>
        </draggable>
      </template>
      <template v-else
        ><p class="py-4 text-sm font-light text-gray-400">{{ $t('common.tableGroup') }}</p>
      </template>
    </div>

    <div
      ref="tableScrollContainer"
      class="z-0 flex-grow bg-white"
      :class="list.length == 0 ? 'overflow-hidden' : 'overflow-auto'">
      <!-- Table -->
      <table :id="tableId" class="w-full" v-resizable="{ tableId, onResizeStart, onResizeEnd }">
        <thead
          class="sticky top-0 z-10 text-xs font-normal text-left text-gray-500 uppercase bg-gray-50"
          ref="ungroupDropZone">
          <draggable
            group="b"
            v-model="localHeaders"
            tag="tr"
            item-key="value"
            @end="onDragEnd"
            @dragend="onDragEnd"
            :disabled="!isDragEnabled">
            <template #item="{ element: header, index }">
              <th
                :key="header.value"
                v-if="header.active"
                :data-value="header.value"
                scope="col"
                class="px-4 py-2 text-xs font-normal text-left text-gray-400 uppercase border border-t-0 border-b-0 cursor-pointer"
                @dragstart="onDragStart($event, header)"
                :draggable="isDragEnabled && header.value !== 'actions'">
                <div class="flex gap-2">
                  {{ header.text }}
                  <span
                    @click="handleSort(index)"
                    v-if="header.value !== 'actions' && !header.isSortHidden && !editMode">
                    <Icon icon="fluent:arrow-sort-20-regular" v-if="sortColumn != index" />
                    <Icon
                      icon="fluent:arrow-sort-up-20-regular"
                      v-if="sortColumn == index && sortOrder == 'asc'" />
                    <Icon
                      icon="fluent:arrow-sort-down-20-regular"
                      v-if="sortColumn == index && sortOrder == 'desc'" />
                  </span>
                </div>
              </th>
            </template>
          </draggable>
        </thead>
        <tbody class="bg-white border divide-y divide-gray-200">
          <template v-if="groupHeaders.length === 0">
            <tr
              v-for="(item, index) in list"
              :key="index"
              :class="{
                'bg-red-100 hover:bg-red-200': item.deleted,
                'bg-theme-100':
                  isSelected(item) || (selectedRowId != undefined && item.id == selectedRowId),
                'select-none hover:bg-gray-50': !isSelected(item),
                'cursor-pointer': clickable,
              }"
              @click.ctrl="multiSelect(item)"
              @contextmenu.prevent="$emit('rightClicked', { item: item, event: $event })"
              @click.shift.prevent="multiSelect(item)">
              <template v-for="header in localHeaders" :key="header.value">
                <template v-if="header.active && header.value != 'actions'">
                  <td
                    :key="header.value"
                    class="py-1.5 border"
                    :class="
                      (header.type == 'check' || header.type === 'status') && !editMode
                        ? 'pl-3'
                        : ''
                    "
                    @click.exact="onRowClick(item)">
                    <slot
                      v-if="header.type == 'custom' || (header.hasInlineEdit && editMode)"
                      name="custom"
                      :item="item"
                      :index="index"
                      :header="header.value">
                    </slot>
                    <component
                      v-else
                      :key="item.boid"
                      :is="getComponent(header)"
                      :item="getItemValue(item, header.value)"
                      :textTrue="header.cellStatusActiveText"
                      :showFalse="false"
                      :color="header.cellStatusColor"
                      :header="header.value"
                      :price="header.price"
                      :searchQuery="searchQuery" />
                  </td>
                </template>
                <template v-else-if="header.value === 'actions'">
                  <td class="py-1.5 whitespace-nowrap">
                    <slot name="actions" :item="item"></slot>
                  </td>
                </template>
              </template>
            </tr>
          </template>
          <template v-else>
            <template v-for="group in groupedData" :key="group.key">
              <tr class="border-t border-theme-500">
                <th
                  :colspan="localHeaders.length"
                  scope="colgroup"
                  class="py-2 pl-4 pr-3 border text-theme-700 bg-theme-100 sm:pl-3 border-theme-500">
                  <div class="flex items-center justify-start gap-3">
                    <button
                      type="button"
                      class="rounded-sm"
                      @click.stop="toggleGroupExpansion(group.key)">
                      <IconChevronDown
                        :class="{
                          'rotate-180 transition-all duration-300': isGroupExpanded(group.key),
                        }"
                        class="size-4" />
                    </button>
                    <p class="text-sm font-light">
                      <template v-if="group.key === 'true'">
                        <span class="flex items-center justify-start gap-2">
                          {{ group.headerName }}: <IconCheckCircle class="size-4 text-theme-500" />
                        </span>
                      </template>
                      <template v-else-if="group.key === 'false'">
                        <span class="flex items-center justify-start gap-2">
                          {{ group.headerName }}: <IconCheckCircle class="text-gray-400 size-4" />
                        </span>
                      </template>
                      <template v-else> {{ group.headerName }}: {{ group.key }}</template>
                    </p>
                  </div>
                </th>
              </tr>
              <template v-if="isGroupExpanded(group.key)">
                <template
                  v-for="nestedItem in group.items"
                  :key="nestedItem.key || nestedItem.boid">
                  <template v-if="nestedItem.items">
                    <tr
                      :class="{
                        'bg-red-100 hover:bg-red-200': nestedItem.deleted,
                        'bg-theme-50 hover:bg-theme-100':
                          isSelected(nestedItem) ||
                          (selectedRowId != undefined && nestedItem.id == selectedRowId),
                        'select-none hover:bg-gray-50': !isSelected(nestedItem),
                        'cursor-pointer': clickable,
                      }"
                      @click.ctrl="multiSelect(nestedItem)"
                      @contextmenu.prevent="
                        $emit('rightClicked', { item: nestedItem, event: $event })
                      "
                      @click.shift.prevent="multiSelect(nestedItem)">
                      <th
                        :colspan="localHeaders.length"
                        scope="colgroup"
                        class="py-2 pl-4 pr-3 text-sm font-semibold text-left text-theme-700 bg-theme-50 sm:pl-3">
                        <div class="flex items-center justify-start gap-3 pl-4">
                          <button
                            type="button"
                            class="rounded-sm"
                            @click.stop="toggleGroupExpansion(nestedItem.key)">
                            <IconChevronDown
                              :class="{ 'rotate-180': isGroupExpanded(nestedItem.key) }"
                              class="size-4" />
                          </button>
                          <p class="text-sm font-light">
                            <template v-if="nestedItem.key === 'true'">
                              <span class="flex items-center justify-start gap-2">
                                {{ nestedItem.headerName }}:
                                <IconCheckCircle class="size-4 text-theme-500" />
                              </span>
                            </template>
                            <template v-else-if="nestedItem.key === 'false'">
                              <span class="flex items-center justify-start gap-2">
                                {{ nestedItem.headerName }}:
                                <IconCheckCircle class="text-gray-400 size-4" />
                              </span>
                            </template>
                            <template v-else>
                              {{ nestedItem.headerName }}: {{ nestedItem.key }}
                            </template>
                          </p>
                        </div>
                      </th>
                    </tr>
                    <template v-if="isGroupExpanded(nestedItem.key)">
                      <template
                        v-for="nestedSubItem in nestedItem.items"
                        :key="nestedSubItem.key || nestedSubItem.boid">
                        <template v-if="nestedSubItem.items">
                          <tr
                            :class="{
                              'bg-red-100 hover:bg-red-200': nestedSubItem.deleted,
                              'border-t border-theme-500': !nestedSubItem.deleted,
                            }">
                            <th
                              :colspan="localHeaders.length"
                              scope="colgroup"
                              class="py-2 pl-4 pr-3 text-sm font-semibold text-left text-theme-700 bg-theme-50 sm:pl-3">
                              <div class="flex items-center justify-start gap-3 pl-12">
                                <p class="text-sm font-light">
                                  <template v-if="nestedSubItem.key === 'true'">
                                    <span class="flex items-center justify-start gap-2">
                                      {{ nestedSubItem.headerName }}:
                                      <IconCheckCircle class="size-4 text-theme-500" />
                                    </span>
                                  </template>
                                  <template v-else-if="nestedSubItem.key === 'false'">
                                    <span class="flex items-center justify-start gap-2">
                                      {{ nestedSubItem.headerName }}:
                                      <IconCheckCircle class="text-gray-400 size-4" />
                                    </span>
                                  </template>
                                  <template v-else>
                                    {{ nestedSubItem.headerName }}: {{ nestedSubItem.key }}
                                  </template>
                                </p>
                              </div>
                            </th>
                          </tr>
                          <template v-if="isGroupExpanded(nestedSubItem.key)">
                            <template
                              v-for="deepNestedItem in nestedSubItem.items"
                              :key="deepNestedItem.boid">
                              <tr
                                :class="{
                                  'bg-red-100 hover:bg-red-200': deepNestedItem.deleted,
                                  'border-t border-gray-300 hover:bg-gray-50':
                                    !deepNestedItem.deleted,
                                  'cursor-pointer': clickable,
                                }">
                                <template v-for="header in localHeaders" :key="header.value">
                                  <template v-if="header.active && header.value != 'actions'">
                                    <td
                                      :key="header.value"
                                      class="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 whitespace-nowrap sm:pl-3"
                                      @click.exact="onRowClick(deepNestedItem)">
                                      <component
                                        v-if="header.type != 'custom'"
                                        :key="deepNestedItem.boid"
                                        :is="getComponent(header)"
                                        :item="getItemValue(deepNestedItem, header.value)"
                                        :textTrue="header.cellStatusActiveText"
                                        :showFalse="false"
                                        :color="header.cellStatusColor"
                                        :header="header.value"
                                        :price="header.price"
                                        :searchQuery="searchQuery" />
                                      <slot
                                        v-else
                                        name="custom"
                                        :item="deepNestedItem"
                                        :index="deepNestedItem.boid"
                                        :header="header.value"></slot>
                                    </td>
                                  </template>
                                </template>
                                <td
                                  class="relative py-4 pl-3 pr-4 text-sm font-medium text-right whitespace-nowrap sm:pr-3">
                                  <slot name="actions" :item="deepNestedItem"></slot>
                                </td>
                              </tr>
                            </template>
                          </template>
                        </template>
                        <template v-else>
                          <tr
                            class="border-t border-gray-300 hover:bg-gray-50"
                            :class="{
                              'bg-red-100 hover:bg-red-200': nestedSubItem.deleted,
                              'border-t border-gray-300 hover:bg-gray-50': !nestedSubItem.deleted,
                              'cursor-pointer': clickable,
                            }">
                            <template v-for="header in localHeaders" :key="header.value">
                              <template v-if="header.active && header.value != 'actions'">
                                <td
                                  :key="header.value"
                                  class="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 whitespace-nowrap sm:pl-3"
                                  @click.exact="onRowClick(nestedSubItem)">
                                  <component
                                    v-if="header.type !== 'custom'"
                                    :key="nestedSubItem.boid"
                                    :is="getComponent(header)"
                                    :item="getItemValue(nestedSubItem, header.value)"
                                    :textTrue="header.cellStatusActiveText"
                                    :showFalse="false"
                                    :color="header.cellStatusColor"
                                    :header="header.value"
                                    :price="header.price"
                                    :searchQuery="searchQuery" />
                                  <slot
                                    v-else
                                    name="custom"
                                    :item="nestedSubItem"
                                    :index="nestedSubItem.boid"
                                    :header="header.value"></slot>
                                </td>
                              </template>
                            </template>
                            <td
                              class="relative py-4 pl-3 pr-4 text-sm font-medium text-right whitespace-nowrap sm:pr-3">
                              <slot name="actions" :item="nestedSubItem"></slot>
                            </td>
                          </tr>
                        </template>
                      </template>
                    </template>
                  </template>
                  <template v-else>
                    <tr
                      :class="{
                        'bg-red-100 hover:bg-red-200': nestedItem.deleted,
                        'bg-theme-50 hover:bg-theme-100':
                          isSelected(nestedItem) ||
                          (selectedRowId != undefined && nestedItem.id == selectedRowId),
                        'select-none hover:bg-gray-50': !isSelected(nestedItem),
                        'cursor-pointer': clickable,
                      }"
                      @click.ctrl="multiSelect(nestedItem)"
                      @contextmenu.prevent="
                        $emit('rightClicked', { item: nestedItem, event: $event })
                      "
                      @click.shift.prevent="multiSelect(nestedItem)">
                      <template v-for="header in localHeaders" :key="header.value">
                        <template v-if="header.active && header.value != 'actions'">
                          <td
                            :key="header.value"
                            class="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 whitespace-nowrap sm:pl-3"
                            @click.exact="onRowClick(nestedItem)">
                            <component
                              v-if="header.type !== 'custom'"
                              :key="nestedItem.boid"
                              :is="getComponent(header)"
                              :item="getItemValue(nestedItem, header.value)"
                              :textTrue="header.cellStatusActiveText"
                              :showFalse="false"
                              :color="header.cellStatusColor"
                              :header="header.value"
                              :price="header.price"
                              :searchQuery="searchQuery" />
                            <slot
                              v-else
                              name="custom"
                              :item="nestedItem"
                              :index="nestedItem.boid"
                              :header="header.value"></slot>
                          </td>
                        </template>
                      </template>
                      <td
                        class="relative py-4 pl-3 pr-4 text-sm font-medium text-right whitespace-nowrap sm:pr-3">
                        <slot name="actions" :item="nestedItem"></slot>
                      </td>
                    </tr>
                  </template>
                </template>
              </template>
            </template>
          </template>
        </tbody>
      </table>
      <BaseEmptyStateAdd
        v-if="list.length <= 0"
        :isAddNewHidden="isAddNewHidden"
        @add-new="emit('addNew')" />
    </div>
    <BaseTablePaginator
      v-if="totalRecords"
      :total-records="totalRecords"
      :number-of-items="totalRecords"
      :items-per-page="currentItemsPerPage"
      @pageChanged="onPageChange"
      v-model="currentPage">
    </BaseTablePaginator>

    <BaseModalConfirm
      v-if="modalMultiDeleteConfirmation"
      @cancel="modalMultiDeleteConfirmation = false"
      @confirm="onDeleteMulti">
      <template #title>{{ $t('common.actions.delete') }}</template>
      <template #text> {{ $t('messages.info.deleteCascade') }}</template>
      <template #buttonText>{{ $t('common.actions.delete') }}</template>
    </BaseModalConfirm>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, computed, nextTick, useSlots } from 'vue';
import vResizable from '@/utils/v-resizable';
import { excelParser } from '@/utils/excel-parser';
import { useDropZone } from '@vueuse/core';
import draggable from 'vuedraggable';

import BaseTableCellText from './BaseTableCellText.vue';
import BaseTableCellCheck from './BaseTableCellCheck.vue';
import BaseTableCellStatus from './BaseTableCellStatus.vue';
import BaseTableCellPill from './BaseTableCellPill.vue';
import BaseTableControls from './BaseTableControls.vue';
import BaseTableMultiSelect from './BaseTableMultiSelect.vue';
import BaseButton from './BaseButton.vue';
import BaseTablePaginator from '@/components/common/BaseTablePaginator.vue';
import BaseModalConfirm from './BaseModalConfirm.vue';
import IconDescAsc from '@/assets/icons/IconDescAsc.vue';
import IconChevronDown from '@/assets/icons/IconChevronDown.vue';
import IconXmark from '@/assets/icons/IconXmark.vue';
import IconCheckCircle from '@/assets/icons/IconCheckCircle.vue';
import BaseEmptyStateAdd from './BaseEmptyStateAdd.vue';
import { Icon } from '@iconify/vue';
import type { Slots } from 'vue';
import { useStateStore } from '@/stores/state';

interface Header {
  value: string;
  text: string;
  type?: string;
  cellStatusActiveText?: string | undefined;
  cellStatusColor?: 'green' | 'red' | 'blue' | undefined;
  active: boolean;
  isSortHidden?: boolean;
  hasInlineEdit?: boolean;
  price?: boolean;
}

interface Item<T = any> {
  [key: string]: T;
}

interface GroupHeader extends Header {
  order: number;
}

interface GroupedData {
  key: string;
  headerName: string;
  items: Item<any>[] | GroupedData[];
}

const props = defineProps<{
  headers: Header[];
  list: Item<any>[];
  selectedItem?: Item<any>;
  tableId: string;
  totalRecords?: number;
  itemsPerPage?: number;
  isSearchHidden?: boolean;
  groupByHeaders?: Header[];
  onlySearch?: boolean;
  isDownloadButtonHidden?: boolean;
  isAddNewHidden?: boolean;
  tableControlsHidden?: boolean;
  clickable?: boolean;
  emitSelectedRows?: boolean;
  hideMultiselectDelete?: boolean;
  hideMultiselectDownload?: boolean;
  hideGrouping?: boolean;
  isSavingLayoutHidden?: boolean;
  disableMultiselect?: boolean;
  isRefreshButtonHidden?: boolean;
  addButtonText?: string;
  editMode?: boolean;
  selectedRowId?: string;
  selectedPage?: number;
  isFilterButtonHidden?: boolean;
}>();

const currentPage = ref<number>(props.selectedPage || 1);
const currentItemsPerPage = ref<number>(props.itemsPerPage || 50);
const sortColumn = ref<number>();
const sortOrder = ref<'asc' | 'desc'>('asc');
const { setTableSelectedPage } = useStateStore();
function onPageChange(changedItem: any) {
  currentItemsPerPage.value = changedItem.itemsPerPage;
  setTableSelectedPage(props.tableId, currentPage.value, currentItemsPerPage.value);
  handleRefreshData();
}

const filtersActive = ref<boolean>(false);

watch(
  () => props.selectedPage,
  (newData) => {
    if (newData) currentPage.value = newData;
    handleRefreshData();
  }
);

const emit = defineEmits([
  'refreshData',
  'applyFilters',
  'resetFilters',
  'deleteMultiselect',
  'addNew',
  'tableItemClicked',
  'selectedRows',
  'rightClicked',
  'emitSelectedItems',
]);

const componentMap: any = {
  text: BaseTableCellText,
  number: BaseTableCellText,
  check: BaseTableCellCheck,
  status: BaseTableCellStatus,
  pill: BaseTableCellPill,
};

const multiSelectedItems = ref<any[]>([]);

function getComponent(header: Header) {
  if (header.type) return componentMap[header.type];
}
const localHeaders = ref<Header[]>([...props.headers]);

function loadColumnOrder(): void {
  const savedOrder = localStorage.getItem(`${props.tableId}-column-order`);
  if (savedOrder) {
    const savedHeaders = JSON.parse(savedOrder);
    localHeaders.value = savedHeaders;
  } else {
    localHeaders.value = [...props.headers];
  }
}

function saveColumnOrder(): void {
  if (isSavingLayout.value) {
    localStorage.setItem(`${props.tableId}-column-order`, JSON.stringify(localHeaders.value));
  }
}

function onDragEnd(): void {
  saveColumnOrder();
  nextTick(() => {
    const headerElements = document.querySelectorAll(`#${props.tableId} th`);
    const updatedHeaders = Array.from(headerElements)
      .map((th) => {
        const value = (th as HTMLElement).getAttribute('data-value');
        return localHeaders.value.find((header) => header.value === value);
      })
      .filter(Boolean) as Header[];

    localHeaders.value = updatedHeaders;
    saveColumnOrder();
  });
}

watch(
  () => props.headers,
  () => {
    localHeaders.value = [...props.headers];
  },
  { deep: true }
);

watch(
  localHeaders,
  () => {
    saveColumnOrder();
  },
  { deep: true }
);

watch(
  () => props.emitSelectedRows,
  () => {
    if (props.emitSelectedRows) {
      emit('selectedRows', multiSelectedItems.value);
      multiSelectedItems.value = [];
    }
  }
);

const isSavingLayout = ref<boolean>(false);

const loadSaveLayoutState = (): void => {
  const savedState = localStorage.getItem(`${props.tableId}-save-layout`);
  isSavingLayout.value = savedState ? JSON.parse(savedState) : false;
};

function isSelected(item: any) {
  const selected =
    multiSelectedItems.value.find(
      (multiSelectedItem) => JSON.stringify(multiSelectedItem) === JSON.stringify(item)
    ) ||
    (props.selectedItem != undefined &&
      ((item.id && props.selectedItem?.id === item.id) || props.selectedItem == item));

  return selected;
}

function multiSelect(item: any) {
  if (props.disableMultiselect) return;
  if (!isSelected(item)) {
    multiSelectedItems.value.push(item);
  } else {
    multiSelectedItems.value = multiSelectedItems.value.filter((value) => {
      return value.id != item.id;
    });
  }
}

const saveColumnSizes = (): void => {
  if (isSavingLayout.value) {
    const sizes = Array.from(document.querySelectorAll(`#${props.tableId} th`)).map(
      (th) => (th as HTMLElement).offsetWidth
    );
    localStorage.setItem(`${props.tableId}-column-sizes`, JSON.stringify(sizes));
  }
};

const isDragEnabled = ref(true);

function onResizeStart() {
  isDragEnabled.value = false;
}

function onResizeEnd() {
  isDragEnabled.value = true;
}

const clearLocalStorage = (): void => {
  localStorage.removeItem(`${props.tableId}-column-order`);
  localStorage.removeItem(`${props.tableId}-column-sizes`);
  localStorage.removeItem(`${props.tableId}-save-layout`);
};

const toggleSaveLayout = (): void => {
  isSavingLayout.value = !isSavingLayout.value;
  localStorage.setItem(`${props.tableId}-save-layout`, JSON.stringify(isSavingLayout.value));
  if (!isSavingLayout.value) {
    clearLocalStorage();
  } else {
    saveColumnOrder();
    saveColumnSizes();
  }
};

const accordionBody = ref<HTMLElement | null>(null);
const currentHeight = ref<string>('0px');
const filterButtonHidden = ref(false);
const slots: Slots = useSlots();
function toggleFilters() {
  if (!slots.filters) return;
  filtersActive.value = !filtersActive.value;
}

function handleSort(index: number) {
  if (sortColumn.value == index) {
    sortOrder.value = sortOrder.value == 'asc' ? 'desc' : 'asc';
  } else {
    sortColumn.value = index;
    sortOrder.value = 'asc';
  }
  handleRefreshData();
}
watch(
  filtersActive,
  (newVal: boolean) => {
    if (newVal && accordionBody.value) {
      currentHeight.value = `${accordionBody.value.scrollHeight}px`;
    } else {
      currentHeight.value = '0px';
    }
  },
  { immediate: true }
);

onMounted(() => {
  loadColumnOrder();
  loadSaveLayoutState();
  if (accordionBody.value) {
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        if (filtersActive.value && entry.target) {
          currentHeight.value = `${entry.target.scrollHeight}px`;
        }
      }
    });
    observer.observe(accordionBody.value);
  }

  if (!slots.filters) filterButtonHidden.value = true;

  if (props.groupByHeaders) {
    for (let index = 0; index < props.groupByHeaders.length; index++) {
      const header = props.groupByHeaders[index];
      groupHeaders.value.push({ ...header, order: index + 1 });
      const localHeader = localHeaders.value.find((h) => h.value === header.value);
      if (localHeader) {
        localHeader.active = false;
      }
    }
  }
});

function onExportToExcel() {
  try {
    if (multiSelectedItems.value.length) {
      excelParser({
        data: multiSelectedItems.value,
        headers: props.headers,
        tableId: props.tableId,
      });
    } else {
      excelParser({ data: props.list, headers: props.headers, tableId: props.tableId });
    }
  } catch (error) {
    console.log('there was an error', error);
  }
}

function getItemValue(obj: any, path: string): any {
  return path.split('.').reduce((acc, part) => acc && acc[part], obj);
}

function onRefreshData() {
  searchQuery.value = '';
  currentPage.value = 1;
  sortColumn.value = 0;
  sortOrder.value = 'asc';
  handleRefreshData();
}

function handleRefreshData() {
  emit('refreshData', {
    query: searchQuery.value,
    page: currentPage.value,
    column: sortColumn.value,
    order: sortOrder.value,
    itemsPerPage: currentItemsPerPage.value,
  });
}

function onApplyFilters() {
  currentPage.value = 1;
  sortColumn.value = 0;
  sortOrder.value = 'asc';
  currentItemsPerPage.value = 0;
  handleApplyFilters();
}

function handleApplyFilters() {
  emit('applyFilters', {
    page: currentPage.value,
    column: sortColumn.value,
    order: sortOrder.value,
    itemsPerPage: currentItemsPerPage.value,
  });
}

const searchQuery = ref<string>('');

function onSearchUpdate() {
  currentPage.value = 1;
  handleRefreshData();
}

const groupDropZone = ref<HTMLElement | null>(null);
const ungroupDropZone = ref<HTMLElement | null>(null);

const groupHeaders = ref<GroupHeader[]>([]);

function onDragStart(event: DragEvent, header: Header) {
  event.dataTransfer?.setData('header/object', JSON.stringify(header));
}

function onDrop(files: File[] | null, event: DragEvent) {
  const headerData = event.dataTransfer?.getData('header/object');
  if (headerData) {
    const header = JSON.parse(headerData);

    const headerExists = groupHeaders.value.some((h) => h.value === header.value);
    if (!headerExists && header.value != 'actions') {
      groupHeaders.value.push({ ...header, order: groupHeaders.value.length + 1 });

      const localHeader = localHeaders.value.find((h) => h.value === header.value);
      if (localHeader) {
        localHeader.active = false;
      }
    }
  }
}

useDropZone(groupDropZone, {
  onDrop,
});

function onUngroup(tag: GroupHeader) {
  const index = groupHeaders.value.findIndex((h) => h.value === tag.value);
  if (index !== -1) {
    groupHeaders.value.splice(index, 1);

    const headerIndex = localHeaders.value.findIndex((h) => h.value === tag.value);
    if (headerIndex === -1) {
      const originalIndex = props.headers.findIndex((h) => h.value === tag.value);
      if (originalIndex !== -1) {
        localHeaders.value.splice(originalIndex, 0, {
          ...props.headers[originalIndex],
          active: true,
        });
      }
    } else {
      localHeaders.value[headerIndex].active = true;
    }
  }

  nextTick(() => {
    const element = document.getElementById(tag.value);
    if (element) {
      element.scrollIntoView();
    }
  });
}

function onUngroupDrop(files: File[] | null, event: DragEvent) {
  const headerData = event.dataTransfer?.getData('header/object');
  if (headerData) {
    try {
      const header: GroupHeader = JSON.parse(headerData);
      const index = groupHeaders.value.findIndex((h) => h.value === header.value);
      if (index !== -1) {
        groupHeaders.value.splice(index, 1);
        const localHeaderIndex = localHeaders.value.findIndex((h) => h.value === header.value);
        if (localHeaderIndex === -1) {
          const originalIndex = props.headers.findIndex((h) => h.value === header.value);
          if (originalIndex !== -1) {
            localHeaders.value.splice(originalIndex, 0, {
              ...props.headers[originalIndex],
              active: true,
            });
          }
        } else {
          localHeaders.value[localHeaderIndex].active = true;
        }
        saveColumnOrder();
      }
    } catch (error) {
      console.error('Failed drop:', error);
    }
  }
}

useDropZone(ungroupDropZone, {
  onDrop: onUngroupDrop,
});

watch(
  groupHeaders,
  (newVal, oldVal) => {
    oldVal.forEach((oldHeader) => {
      if (!newVal.some((newHeader) => newHeader.value === oldHeader.value)) {
        const originalHeader = props.headers.find((h) => h.value === oldHeader.value);
        if (originalHeader) {
          const headerIndex = localHeaders.value.findIndex((h) => h.value === oldHeader.value);
          if (headerIndex === -1) {
            const originalIndex = props.headers.findIndex((h) => h.value === oldHeader.value);
            if (originalIndex !== -1) {
              localHeaders.value.splice(originalIndex, 0, {
                ...props.headers[originalIndex],
                active: true,
              });
            }
          } else {
            localHeaders.value[headerIndex].active = true;
          }
        }
      }
    });
  },
  { deep: true }
);

function groupBy(
  array: Item<any>[],
  keys: GroupHeader[],
  sortOrders: Record<string, boolean>
): Item<any>[] | GroupedData[] {
  if (keys.length === 0) {
    return array;
  }

  const [firstKey, ...restKeys] = keys;

  const grouped = array.reduce((result: Record<string, Item<any>[]>, item) => {
    const key = getItemValue(item, firstKey.value);
    if (!result[key]) {
      result[key] = [];
    }
    result[key].push(item);
    return result;
  }, {});

  const sortedKeys = Object.keys(grouped).sort((a, b) => {
    const order = sortOrders[firstKey.value] ? 1 : -1;
    if (a < b) return -1 * order;
    if (a > b) return 1 * order;
    return 0;
  });

  return sortedKeys.map((key) => ({
    key,
    headerName: firstKey.text,
    items: groupBy(grouped[key], restKeys, sortOrders),
    ...grouped[key][0],
  }));
}

const groupedData = computed<Item<any>[] | GroupedData[]>(() => {
  if (groupHeaders.value.length === 0) {
    return props.list;
  }
  return groupBy(props.list, groupHeaders.value, sortOrders.value);
});

const sortOrders = ref<Record<string, boolean>>({});

const toggleSortOrder = (tag: GroupHeader) => {
  sortOrders.value[tag.value] = !sortOrders.value[tag.value];
};

const isAscending = (tag: GroupHeader) => {
  return sortOrders.value[tag.value] !== false;
};

const expandedGroups = ref<Record<string, boolean>>({});

function toggleGroupExpansion(key: string) {
  // initialize to true in case it is undefined, then toggle
  if (expandedGroups.value[key] === undefined) {
    expandedGroups.value[key] = true;
  }
  expandedGroups.value[key] = !expandedGroups.value[key];
}

function isGroupExpanded(key: string): boolean {
  return expandedGroups.value[key] ?? true;
}

const modalMultiDeleteConfirmation = ref(false);

function onDeleteMulti() {
  modalMultiDeleteConfirmation.value = false;
  emit('deleteMultiselect', multiSelectedItems.value);
  multiSelectedItems.value = [];
}
function onRowClick(item: Item<any>) {
  if (!props.clickable) return;
  emit('tableItemClicked', item);
}

function handleExtraButtonClicked() {
  emit('emitSelectedItems', multiSelectedItems.value);
  multiSelectedItems.value = [];
}

// Reset scroll position when list is empty
const tableScrollContainer = ref<HTMLDivElement | null>(null);

watch(
  () => props.list,
  async (newList) => {
    if (newList.length === 0 && tableScrollContainer.value) {
      await nextTick();
      tableScrollContainer.value.scrollLeft = 0;
      tableScrollContainer.value.scrollTop = 0;
    }
  },
  { deep: true }
);
</script>
